import { apiCall } from "@/common/api/ApiResult.model";
import { ListResult } from "@/common/models";
import { eventParser } from "@/gql/event-parser";
import { gql2, formatInput } from "@/gql/lib";
import { inclusiveToDate } from "@/utils/date";
import {
  EventsFilter,
  CoordinateInputTier3,
  ExternalEventType,
  JustificationStatus,
  MarkedStatus,
} from "@/gql/types";
import axios from "axios";
import { getMarkFilterValue } from "../mark-filter.util";

const ENABLE_BACKEND_EVENT_TYPE_FILTER = true;

export const getEventsForCluster = gql2<
  {
    clusterUID: string;
    cords: CoordinateInputTier3[];
    cursor: string;
    mark?: MarkedStatus | null;
    justification?: JustificationStatus | null;
    externalEventTypes?: ExternalEventType[] | null;
    fromDate?: string | null;
    toDate?: string | null;
  },
  ListResult<any>
>(
  "tier3-events",
  (
    {
      clusterUID,
      cursor,
      cords,
      mark,
      justification,
      externalEventTypes,
      fromDate,
      toDate,
    },
    { isInfoTypeChildOfOne, default_label, indexed_insights },
  ) => {
    const q: EventsFilter = {
      resolveJustification: true,
      coordinates: cords,
      clusterUID,
      cursor,
    };
    const markFilterValue = getMarkFilterValue(mark);
    if (markFilterValue !== undefined) {
      q.mark = markFilterValue;
    }
    if (justification) {
      q.justification = justification;
    }
    if (ENABLE_BACKEND_EVENT_TYPE_FILTER && externalEventTypes?.length) {
      q.externalEventTypes = externalEventTypes;
    }
    if (fromDate) {
      q.fromDate = fromDate;
    }
    if (toDate) {
      // the filter's end date is the last day the user wants to see, and the
      // query's is exclusive — see inclusiveToDate
      q.toDate = inclusiveToDate(toDate);
    }
    return {
      query: `{
        #EXPORT
        events(filter:${formatInput(q)}){
          cursor
          id
          subType
          justification{
            id
            status
          }
          labels{
            id
            name
            type
            labelType
            labelCategory
            concreteScore
          }
          date
          sourceApplication
          insights{
            id
            name
            description
            severity
          }
          items{
            id
            name
            subType
            category
            clusterUID
            extendedProperties{
              topic
            }
            archive{
              archiveName
              siblings
            }
            internalItems{
              name
              labels
            }
            labels{
              infoLabel{
                id
                name
              }
              adhocLabels{
                id
                name
              }
              piis{
                id
                name
                labelCategory
              }
              ctxLabels{
                id
                name
              }
            }
          }
          severity
          severityLevel
          mark
          triage{
            decision
            desc
          }
          relatedEntities{
            fileUploadPath
            exposedBy{
              entity{
                id
                email
                name
                type
                isExternal
                department
              }
            }
            exposedTo{
              relationType
              entity{
                id
                isExternal
                isPersonalAddress
                name
                type
                email
                department
              }
            }
          }
        }
        #EXPORT_END
      }`,
      mapper: (res) => {
        let cursor = "";
        if (res.events.length > 0) {
          cursor = res.events[0].cursor;
        }
        return {
          cursor,
          objects: res.events.map(eventParser).map((item: any) => {
            return {
              ...item,
              disableDrilldown: true,
            };
          }),
        };
      },
    };
  },
);

export const get_cluster_label = apiCall<string, string>(
  "cluster.get-label",
  async (cluster_id) => {
    const res = await axios.get(
      `/label-api/cluster/${cluster_id}?resolve=false`,
    );
    return {
      success: true,
      value: res.data.label || "",
    };
  },
);
